import { useState } from "react";
import { ShieldCheck, Eye, EyeOff, AlertCircle, CheckCircle2 } from "lucide-react";
import API_BASE_URL from "../../ScrapFlow/config/config.server";
import { setAdminToken } from "./adminApi";

interface Props {
  onLogin: () => void;
}

export default function AdminLogin({ onLogin }: Props) {
  const [mode, setMode] = useState<"login" | "create">("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Create-account (bootstrap) fields — only usable once, before any admin exists.
  const [bootstrapSecret, setBootstrapSecret] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [createSuccess, setCreateSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success || !data.token) {
        setError(data?.message || "Invalid credentials. Access denied.");
        return;
      }
      setAdminToken(data.token);
      onLogin();
    } catch (err) {
      setError("Unable to reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/auth/bootstrap`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secret: bootstrapSecret, username: newUsername, password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setError(data?.message || "Could not create admin account.");
        return;
      }
      setCreateSuccess(true);
      setUsername(newUsername);
      setPassword("");
      setBootstrapSecret("");
      setNewUsername("");
      setNewPassword("");
    } catch (err) {
      setError("Unable to reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next: "login" | "create") => {
    setMode(next);
    setError("");
    setCreateSuccess(false);
  };

  return (
    <div className="min-h-screen bg-[#060b18] flex items-center justify-center p-4">
      <div className="relative w-full max-w-sm sm:max-w-md">
        <div className="bg-[#0c1428] border border-white/[0.07] rounded-2xl p-6 sm:p-8 shadow-2xl shadow-black/60">
          <div className="flex flex-col items-center mb-7 sm:mb-8">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#00c9a7]/10 border border-[#00c9a7]/20 flex items-center justify-center mb-3 sm:mb-4">
              <ShieldCheck className="w-6 h-6 sm:w-7 sm:h-7 text-[#00c9a7]" />
            </div>
            <h1 className="text-white text-lg sm:text-xl font-semibold tracking-tight text-center">Admin Control Panel</h1>
            <p className="text-[#6b7fa3] text-xs sm:text-sm mt-1 text-center">
              {mode === "login" ? "Restricted access — authorized personnel only" : "One-time setup — creates the first superadmin account"}
            </p>
          </div>

          {mode === "login" ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[#a0aec0] text-xs font-medium tracking-widest uppercase">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  className="w-full bg-[#0a1020] border border-white/[0.08] text-white rounded-xl px-4 py-3 text-sm placeholder:text-[#3d4f6a] focus:outline-none focus:border-[#00c9a7]/40 focus:ring-2 focus:ring-[#00c9a7]/10 transition-all"
                  autoComplete="username"
                  autoCapitalize="none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[#a0aec0] text-xs font-medium tracking-widest uppercase">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full bg-[#0a1020] border border-white/[0.08] text-white rounded-xl px-4 py-3 pr-12 text-sm placeholder:text-[#3d4f6a] focus:outline-none focus:border-[#00c9a7]/40 focus:ring-2 focus:ring-[#00c9a7]/10 transition-all"
                    autoComplete="current-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3d4f6a] hover:text-[#6b7fa3] transition-colors p-2">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl px-4 py-3 text-sm">
                  <AlertCircle size={14} className="shrink-0" />
                  {error}
                </div>
              )}

              <button type="submit" disabled={loading || !username || !password} className="w-full min-h-[48px] bg-[#00c9a7] hover:bg-[#00b396] disabled:bg-[#00c9a7]/30 disabled:cursor-not-allowed text-[#060b18] font-semibold rounded-xl py-3 text-sm transition-all duration-200 flex items-center justify-center gap-2 mt-2">
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#060b18]/30 border-t-[#060b18] rounded-full animate-spin" />
                    Authenticating…
                  </>
                ) : (
                  "Access Dashboard"
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[#a0aec0] text-xs font-medium tracking-widest uppercase">Bootstrap Secret</label>
                <input
                  type="password"
                  value={bootstrapSecret}
                  onChange={(e) => setBootstrapSecret(e.target.value)}
                  placeholder="ADMIN_BOOTSTRAP_SECRET"
                  className="w-full bg-[#0a1020] border border-white/[0.08] text-white rounded-xl px-4 py-3 text-sm placeholder:text-[#3d4f6a] focus:outline-none focus:border-[#00c9a7]/40 focus:ring-2 focus:ring-[#00c9a7]/10 transition-all"
                  autoComplete="off"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[#a0aec0] text-xs font-medium tracking-widest uppercase">Username</label>
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Choose a username"
                  className="w-full bg-[#0a1020] border border-white/[0.08] text-white rounded-xl px-4 py-3 text-sm placeholder:text-[#3d4f6a] focus:outline-none focus:border-[#00c9a7]/40 focus:ring-2 focus:ring-[#00c9a7]/10 transition-all"
                  autoComplete="off"
                  autoCapitalize="none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[#a0aec0] text-xs font-medium tracking-widest uppercase">Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full bg-[#0a1020] border border-white/[0.08] text-white rounded-xl px-4 py-3 text-sm placeholder:text-[#3d4f6a] focus:outline-none focus:border-[#00c9a7]/40 focus:ring-2 focus:ring-[#00c9a7]/10 transition-all"
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl px-4 py-3 text-sm">
                  <AlertCircle size={14} className="shrink-0" />
                  {error}
                </div>
              )}

              {createSuccess && (
                <div className="flex items-center gap-2 bg-[#00c9a7]/10 border border-[#00c9a7]/20 text-[#00c9a7] rounded-xl px-4 py-3 text-sm">
                  <CheckCircle2 size={14} className="shrink-0" />
                  Admin account created. You can now log in.
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !bootstrapSecret || !newUsername || newPassword.length < 8}
                className="w-full min-h-[48px] bg-[#00c9a7] hover:bg-[#00b396] disabled:bg-[#00c9a7]/30 disabled:cursor-not-allowed text-[#060b18] font-semibold rounded-xl py-3 text-sm transition-all duration-200 flex items-center justify-center gap-2 mt-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#060b18]/30 border-t-[#060b18] rounded-full animate-spin" />
                    Creating…
                  </>
                ) : (
                  "Create Admin Account"
                )}
              </button>
            </form>
          )}

          <button
            type="button"
            onClick={() => switchMode(mode === "login" ? "create" : "login")}
            className="w-full text-center text-[#6b7fa3] hover:text-[#00c9a7] text-xs mt-5 transition-colors"
          >
            {mode === "login" ? "Create an admin account" : "Back to login"}
          </button>

          <p className="text-center text-[#3d4f6a] text-xs mt-3">All access attempts are logged and monitored</p>
        </div>

        <p className="text-center text-[#2a3a52] text-xs mt-4 font-mono">MarketAdmin v2.4.1 · 2026</p>
      </div>
    </div>
  );
}
